import { useState, useEffect } from "react";
import { Menu, X, LogIn, LayoutDashboard, User, ChevronDown, Bell, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nProvider";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Link, useLocation } from "react-router-dom";
import AuthModal from "./AuthModal";
import { useAuth } from "@/contexts/AuthContext";
import { getSectionContent } from "@/data/cmsStore";
import { useAlerts } from "@/hooks/useAlerts";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import NotificationOverlay from "./NotificationOverlay";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const { t, isRtl, lang } = useI18n();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const { isAuthenticated, user, logout } = useAuth();
  const { unreadCount } = useAlerts(user?.id);
  
  const [cmsNavbarContent, setCmsNavbarContent] = useState(() => getSectionContent("navbar", "global"));

  useEffect(() => {
    const handleUpdate = (e: any) => {
      if (!e.detail || e.detail.page === "global" || e.detail.page === "all") {
        setCmsNavbarContent(getSectionContent("navbar", "global"));
      }
    };
    window.addEventListener("cmsLayoutDraftChanged", handleUpdate);
    window.addEventListener("cmsLayoutChanged", handleUpdate);
    return () => {
      window.removeEventListener("cmsLayoutDraftChanged", handleUpdate);
      window.removeEventListener("cmsLayoutChanged", handleUpdate);
    };
  }, []);

  const navStyle = (cmsNavbarContent?.style as 'classic' | 'minimalist' | 'megamenu' | 'hamburger') || 'classic';
  const bgImageUrl = cmsNavbarContent?.bgImageUrl || '';

  const defaultRawLinks = [
    { label_fr: "Pitcher", hover_fr: "Attentes", label_ar: "اقتراح", hover_ar: "الانتظارات", label_zgh: "ⵙⵎⵏⵉⴷ", hover_zgh: "ⵜⵉⵔⵎⵉⵜⵉⵏ", href: "/pitcher" },
    { label_fr: "Poster", hover_fr: "Kayn'in TALK", label_ar: "نقاش", hover_ar: "تقاسم", label_zgh: "ⴼⵙⵔ", hover_zgh: "ⵜⴰⵡⴰⵍⵜ", href: "/poster" },
    { label_fr: "Scroller", hover_fr: "Evènements", label_ar: "تصفح", hover_ar: "أحداث", label_zgh: "ⵣⵣⵔⵉ", hover_zgh: "ⵉⵏⵎⵓⵇⵇⴰⵔⵏ", href: "/scroller" },
    { label_fr: "Watcher", hover_fr: "Émissions", label_ar: "مشاهدة", hover_ar: "برامج", label_zgh: "ⴰⵏⵏⴰⵢ", hover_zgh: "ⴰⵙⴰⵔⵓ", href: "/watcher" },
    { label_fr: "Politiquer", hover_fr: "Partis politiques", label_ar: "سياسة", hover_ar: "الأحزاب", label_zgh: "ⵙⴽⵔ ⵜⴰⵙⵔⵜⵉⵜ", hover_zgh: "ⵉⴽⴰⴱⴰⵕⵏ", href: "/politiquer" },
    { label_fr: "Logguer", hover_fr: "Digitalisation", label_ar: "رقمنة", hover_ar: "منصات", label_zgh: "ⵙⵎⵓⵟⵟⵓⵏ", hover_zgh: "ⴰⵙⵎⵓⵟⵟⵓⵏ", href: "/logguer" },
  ];

  let rawLinks = [...defaultRawLinks];

  if (cmsNavbarContent?.links) {
    try {
      const parsed = JSON.parse(cmsNavbarContent.links);
      if (parsed && Array.isArray(parsed) && parsed.length > 0) {
        rawLinks = parsed.map((pLink: any, i: number) => {
          const dLink = defaultRawLinks.find(d => d.href === pLink.href) || defaultRawLinks[i] || {} as any;
          return {
            ...pLink,
            label_fr: dLink.label_fr || pLink.label_fr,
            label_ar: dLink.label_ar || pLink.label_ar,
            label_zgh: dLink.label_zgh || pLink.label_zgh,
            hover_fr: dLink.hover_fr || pLink.hover_fr,
            hover_ar: dLink.hover_ar || pLink.hover_ar,
            hover_zgh: dLink.hover_zgh || pLink.hover_zgh,
          };
        });
      }
    } catch (e) {
      // keep default
    }
  }

  const links = rawLinks.map(l => {
    let finalHref = l.href || "/";
    // Convert legacy hash links to clean route paths automatically
    if (finalHref === "#pitcher") finalHref = "/attentes-citoyennes";
    if (finalHref === "#talk") finalHref = "/accueil-talk";
    if (finalHref === "#agenda-national") finalHref = "/agenda-national";
    if (finalHref === "#emissions") finalHref = "/accueil-emissions";
    if (finalHref === "#partis") finalHref = "/partis";
    if (finalHref === "#actions") finalHref = "/actions";

    if (isHome && finalHref.startsWith("/#")) finalHref = finalHref.substring(1);
    if (!isHome && finalHref.startsWith("#")) finalHref = "/" + finalHref;
    return { ...l, href: finalHref };
  });

  const megamenuGroups: Record<string, typeof links> = {};
  if (navStyle === 'megamenu') {
    links.forEach(link => {
      const cat = lang === 'zgh' && link.category_zgh ? link.category_zgh : isRtl ? (link.category_ar || link.category_fr || "Général") : (link.category_fr || "Général");
      if (!megamenuGroups[cat]) megamenuGroups[cat] = [];
      megamenuGroups[cat].push(link);
    });
  }

  const renderAuthAlerts = (isMinimalist: boolean = false) => (
    <>
      {isAuthenticated && (
        <button onClick={() => setIsNotificationOpen(true)} className="relative p-2 text-muted-foreground hover:text-foreground transition-colors group">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute top-0 right-0 flex items-center justify-center w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full border-2 border-background">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
          {isMinimalist && <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] opacity-0 group-hover:opacity-100 bg-border px-1.5 rounded text-foreground whitespace-nowrap transition-opacity">{lang === 'zgh' ? "ⵉⵏⵖⵎⵉⵙⵏ" : isRtl ? "إشعارات" : "Alertes"}</span>}
        </button>
      )}
      {isAuthenticated ? (
        user?.role === "CITIZEN" ? (
          isMinimalist ? (
            <DropdownMenu>
               <DropdownMenuTrigger className="p-2 text-emerald-600 hover:text-emerald-700 transition-colors hover:bg-emerald-50 rounded-full relative group outline-none">
                 <User className="w-5 h-5" />
               </DropdownMenuTrigger>
               <DropdownMenuContent align="end" className="w-40">
                 <DropdownMenuItem disabled className="font-semibold text-emerald-600 justify-center">Connecté</DropdownMenuItem>
                 <DropdownMenuItem onClick={logout} className="text-red-600 cursor-pointer justify-center">Déconnexion</DropdownMenuItem>
               </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="font-body font-semibold gap-2 text-emerald-600 border-emerald-200 hover:bg-emerald-50">
                  <User className="w-4 h-4" /> {lang === 'zgh' ? "ⵉⵎⵎⴰⵏ" : isRtl ? "متصل" : "Connecté"} <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                 <DropdownMenuItem onClick={logout} className="text-red-600 cursor-pointer justify-center font-semibold">Déconnexion</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )
        ) : (
          isMinimalist ? (
            <DropdownMenu>
               <DropdownMenuTrigger className="p-2 text-primary hover:text-primary/80 transition-colors hover:bg-primary/5 rounded-full relative group outline-none">
                 <User className="w-5 h-5" />
               </DropdownMenuTrigger>
               <DropdownMenuContent align="end" className="w-48">
                 <DropdownMenuItem disabled className="font-semibold text-primary justify-center">Connecté</DropdownMenuItem>
                 <DropdownMenuItem asChild className="cursor-pointer justify-center">
                    {user?.role === 'ADMIN' ? (
                      <Link to="/in" className="flex items-center gap-2 w-full"><LayoutDashboard className="w-4 h-4" /> {lang === 'zgh' ? "ⴰⵎⵉⴹⴰⵏ ⵉⵏⵓ" : isRtl ? "حسابي" : "Mon espace"}</Link>
                    ) : (
                      <Link to="/dashboard" className="flex items-center gap-2 w-full"><LayoutDashboard className="w-4 h-4" /> {lang === 'zgh' ? "ⴰⵎⵉⴹⴰⵏ ⵉⵏⵓ" : isRtl ? "حسابي" : "Mon espace"}</Link>
                    )}
                 </DropdownMenuItem>
                 <DropdownMenuItem onClick={logout} className="text-red-600 cursor-pointer justify-center w-full">Déconnexion</DropdownMenuItem>
               </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="secondary" className="font-body font-semibold gap-2">
                  <User className="w-4 h-4" /> {lang === 'zgh' ? "ⵉⵎⵎⴰⵏ" : isRtl ? "متصل" : "Connecté"} <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                 <DropdownMenuItem asChild className="cursor-pointer justify-center font-semibold text-primary">
                    {user?.role === 'ADMIN' ? (
                      <Link to="/in" className="flex items-center gap-2 w-full"><LayoutDashboard className="w-4 h-4" /> {lang === 'zgh' ? "ⴰⵎⵉⴹⴰⵏ ⵉⵏⵓ" : isRtl ? "حسابي" : "Mon espace"}</Link>
                    ) : (
                      <Link to="/dashboard" className="flex items-center gap-2 w-full"><LayoutDashboard className="w-4 h-4" /> {lang === 'zgh' ? "ⴰⵎⵉⴹⴰⵏ ⵉⵏⵓ" : isRtl ? "حسابي" : "Mon espace"}</Link>
                    )}
                 </DropdownMenuItem>
                 <DropdownMenuItem onClick={logout} className="text-red-600 cursor-pointer justify-center font-semibold w-full">Déconnexion</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )
        )
      ) : (
        isMinimalist ? (
          <button onClick={() => setIsAuthModalOpen(true)} className="p-2 text-muted-foreground hover:text-primary transition-colors hover:bg-primary/5 rounded-full relative group">
            <LogIn className="w-5 h-5" />
            <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] opacity-0 group-hover:opacity-100 bg-border px-1.5 rounded text-foreground whitespace-nowrap transition-opacity">{lang === 'zgh' ? "ⴰⴽⵛⵛⵓⵎ" : isRtl ? "دخول" : "Connexion"}</span>
          </button>
        ) : (
          <Button size="sm" variant="secondary" className="font-body font-semibold gap-2" onClick={() => setIsAuthModalOpen(true)}>
            <LogIn className="w-4 h-4" /> {lang === 'zgh' ? "ⴽⵛⵎ" : isRtl ? "تسجيل الدخول" : "Se connecter"}
          </Button>
        )
      )}
    </>
  );

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 ${bgImageUrl ? 'border-b border-border/50 bg-background/30' : 'bg-background/80 backdrop-blur-lg border-b border-border/50'}`}
      style={bgImageUrl ? { 
        backgroundImage: `url(${bgImageUrl})`, 
        backgroundSize: 'cover', 
        backgroundPosition: 'center' 
      } : undefined}
    >
      <div className="container flex items-center justify-between h-16">
        <Link to="/" dir="ltr" translate="no" className="notranslate flex items-end gap-0 shrink-0 hover:scale-105 transition-transform duration-300 select-none pb-1" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <div className="flex flex-col justify-end">
            <div dir="ltr" className="flex items-center justify-between w-full text-[7.5px] md:text-[8.5px] font-bold text-[#0a2342]/70 uppercase tracking-widest select-none mb-[-2px]">
              <span>ⴽⴰⵢⵏⵉⵏ</span>
              <span className="w-[3px] h-[3px] rounded-full bg-[#ff6b00]"></span>
              <span dir="rtl">كاينين</span>
            </div>
            <span dir="ltr" className="font-display font-black text-[28px] md:text-[34px] leading-[0.9] text-[#0a2342] tracking-tight">Kayn</span>
          </div>
          <span className="w-1.5 h-[12px] md:w-2 md:h-3.5 bg-[#ff6b00] inline-block mb-[16px] md:mb-[22px] mx-[2px] md:mx-[3px] rounded-[1px] transform -skew-x-6"></span>
          <span dir="ltr" className="font-display font-black text-[28px] md:text-[34px] leading-[0.9] text-[#0a2342] tracking-tight">in</span>
        </Link>

        {/* ─── DESKTOP NAVIGATION ─── */}
        <div className="hidden md:flex items-center flex-1 justify-end xl:justify-between ms-10">
          
          {/* Links Area */}
          <div className="flex items-center gap-1.5 lg:gap-3 flex-1 justify-start">
            {navStyle === 'hamburger' ? (
              <div className="text-sm font-semibold text-muted-foreground/50 hidden xl:block px-4">
                {lang === 'zgh' ? "ⵓⵎⵓⵖ ⵉⴼⵙⵙⵓⵙⵏ" : isRtl ? "قائمة مبسطة مفعلة" : "Menu simplifié activé"}
              </div>
            ) : navStyle === 'megamenu' ? (
              <div className="flex items-center gap-2">
                {Object.entries(megamenuGroups).map(([cat, catLinks]) => (
                  <DropdownMenu key={cat}>
                    <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-display font-bold uppercase tracking-wider text-foreground/80 hover:text-primary hover:bg-primary/5 px-3 py-1.5 rounded-full transition-all border border-transparent hover:border-primary/20 focus:outline-none">
                      {cat} <ChevronDown className="w-3 h-3" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48 bg-background/95 backdrop-blur-sm border-border/50">
                      {catLinks.map((link, idx) => (
                        <DropdownMenuItem key={idx} asChild className="cursor-pointer font-semibold font-body">
                          {link.href.startsWith("#") ? (
                            <a href={link.href}>{lang === 'zgh' && link.label_zgh ? link.label_zgh : isRtl ? link.label_ar : link.label_fr}</a>
                          ) : (
                            <Link to={link.href}>{lang === 'zgh' && link.label_zgh ? link.label_zgh : isRtl ? link.label_ar : link.label_fr}</Link>
                          )}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ))}
              </div>
            ) : (
              // Classic or Minimalist Links
              <div className="flex items-center gap-1 lg:gap-2">
                {links.map((link, idx) => {
                  const label = lang === 'zgh' && link.label_zgh ? link.label_zgh : isRtl ? link.label_ar : link.label_fr;
                  const hoverText = lang === 'zgh' && link.hover_zgh ? link.hover_zgh : isRtl ? link.hover_ar : link.hover_fr;
                  const langStyles = lang === 'ar' 
                    ? 'text-[15px] lg:text-base tracking-normal px-4 font-bold' 
                    : lang === 'zgh' 
                    ? 'text-[13px] lg:text-sm tracking-wide px-3 font-bold uppercase' 
                    : 'text-[11.5px] lg:text-[13.5px] tracking-wider px-1.5 font-bold uppercase';
                  const baseClass = `group flex items-center ${langStyles} font-display text-foreground/80 hover:text-primary hover:bg-primary/5 ${navStyle === 'minimalist' ? 'py-1 scale-95' : 'py-1.5'} rounded-full transition-all border border-transparent hover:border-primary/20 whitespace-nowrap`;
                  return link.href.startsWith("#") ? (
                    <a key={idx} href={link.href} className={baseClass}>
                      <span>{label}</span>
                      {hoverText && (
                        <span className="max-w-0 overflow-hidden group-hover:max-w-[200px] transition-all duration-500 ease-out opacity-0 group-hover:opacity-100 flex items-center">
                          <span className={isRtl ? "mr-1" : "ml-1"}>({hoverText})</span>
                        </span>
                      )}
                    </a>
                  ) : (
                    <Link key={idx} to={link.href} className={baseClass}>
                      <span>{label}</span>
                      {hoverText && (
                        <span className="max-w-0 overflow-hidden group-hover:max-w-[200px] transition-all duration-500 ease-out opacity-0 group-hover:opacity-100 flex items-center">
                          <span className={isRtl ? "mr-1" : "ml-1"}>({hoverText})</span>
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* Actions Area */}
          <div className="flex items-center gap-2 lg:gap-3 shrink-0">
            {navStyle !== 'hamburger' && (
              <div className={`hidden lg:flex items-center gap-2 border-border/50 border-e pe-2 lg:pe-3 ${navStyle === 'minimalist' ? 'me-0' : 'me-1'}`}>
                {navStyle === 'minimalist' ? (
                  <>
                    <Button size="sm" variant="ghost" className="rounded-full font-bold text-xs text-red-600 hover:text-red-700 hover:bg-red-50 animate-pulse flex items-center gap-1.5" asChild>
                      <Link to="/ring">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-600"></span>
                        {lang === 'zgh' ? "ⴰⴱⴰⵔⴰⵣ" : isRtl ? "الحلبة" : "Le Ring"}
                      </Link>
                    </Button>
                    <Button size="sm" className="rounded-full font-bold text-xs bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white border-0" asChild>
                      <Link to="/match" className="flex items-center gap-1">
                        <Flame className="w-3.5 h-3.5 text-yellow-300 animate-pulse" />
                        {lang === 'zgh' ? "ⵙⵡⴰⵢⴱ" : isRtl ? "سوايب" : "Swipe"}
                      </Link>
                    </Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" className="rounded-full font-bold shadow-lg shadow-red-500/50 hover:scale-105 transition-transform bg-red-600 hover:bg-red-700 text-white animate-pulse flex items-center gap-1.5" asChild>
                      <Link to="/ring">
                        <span className="w-1.5 h-1.5 rounded-full bg-white"></span>
                        {lang === 'zgh' ? "ⴰⴱⴰⵔⴰⵣ" : isRtl ? "الحلبة" : "Le Ring"}
                      </Link>
                    </Button>
                    <Button size="sm" className="rounded-full font-bold shadow-lg shadow-rose-500/30 hover:scale-105 transition-transform bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white border-0" asChild>
                      <Link to="/match" className="flex items-center gap-1">
                        <Flame className="w-4 h-4 text-yellow-300 animate-pulse" />
                        {lang === 'zgh' ? "ⵙⵡⴰⵢⴱ" : isRtl ? "سوايب" : "Swipe"}
                      </Link>
                    </Button>
                  </>
                )}
              </div>
            )}

            <LanguageSwitcher iconOnly={navStyle === 'minimalist'} />
            
            {renderAuthAlerts(navStyle === 'minimalist')}
            
            {navStyle === 'hamburger' && (
              <button className="text-foreground ml-2 p-2 hover:bg-muted rounded-full" onClick={() => setOpen(!open)}>
                 <Menu className="w-6 h-6" />
              </button>
            )}
          </div>
        </div>

        {/* ─── MOBILE BURGER & AUTH ─── */}
        <div className="md:hidden flex items-center gap-1">
          {renderAuthAlerts(true)}
          <button className="text-foreground ml-1 p-1.5 rounded-full hover:bg-muted transition-colors" onClick={() => setOpen(!open)}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* ─── MOBILE DRAWER (et Desktop si Hamburger mode) ─── */}
      {open && (
        <div className="md:hidden lg:hidden bg-background border-t border-border px-6 py-4 space-y-3 absolute top-16 left-0 right-0 shadow-lg" style={{ display: open && navStyle !== 'hamburger' ? 'block' : undefined }}>
          <div className="max-h-[80vh] overflow-y-auto">
            {links.map((link, idx) => {
              const label = lang === 'zgh' && link.label_zgh ? link.label_zgh : isRtl ? link.label_ar : link.label_fr;
              const hoverText = lang === 'zgh' && link.hover_zgh ? link.hover_zgh : isRtl ? link.hover_ar : link.hover_fr;
              const langStyles = lang === 'ar' ? 'text-lg tracking-normal' : lang === 'zgh' ? 'text-sm tracking-wide uppercase' : 'text-xs tracking-widest uppercase';
              const baseClass = `group flex items-center ${langStyles} font-display font-bold text-foreground/80 hover:text-primary hover:bg-primary/5 px-4 py-2 rounded-xl transition-colors`;
              return link.href.startsWith("#") ? (
                <a key={idx} href={link.href} className={baseClass} onClick={() => setOpen(false)}>
                  <span>{label}</span>
                  {hoverText && (
                    <span className="opacity-70 font-normal ms-1">({hoverText})</span>
                  )}
                </a>
              ) : (
                <Link key={idx} to={link.href} className={baseClass} onClick={() => setOpen(false)}>
                  <span>{label}</span>
                  {hoverText && (
                    <span className="opacity-70 font-normal ms-1">({hoverText})</span>
                  )}
                </Link>
              );
            })}
            <div className="py-2"><LanguageSwitcher /></div>
            
            <div className="flex flex-col gap-2 py-2 border-y border-border/50 my-2">
              <Button size="sm" className="w-full rounded-xl font-bold shadow-lg shadow-red-500/30 bg-red-600 hover:bg-red-700 text-white animate-pulse flex items-center justify-center gap-2" asChild>
                 <Link to="/ring" onClick={() => setOpen(false)}>
                   <span className="w-2 h-2 rounded-full bg-white"></span>
                   {lang === 'zgh' ? "ⴰⴱⴰⵔⴰⵣ" : isRtl ? "الحلبة" : "Le Ring"}
                 </Link>
              </Button>
              <Button size="sm" className="w-full rounded-xl font-bold shadow-sm bg-gradient-to-r from-rose-500 to-pink-600 text-white hover:from-rose-600 hover:to-pink-700" asChild>
                 <Link to="/match" onClick={() => setOpen(false)} className="flex items-center justify-center gap-2">
                   <Flame className="w-4 h-4 text-yellow-300 animate-pulse" />
                   {lang === 'zgh' ? "ⵙⵡⴰⵢⴱ" : isRtl ? "سوايب" : "Swipe"}
                 </Link>
              </Button>
            </div>

            {isAuthenticated && (
              <button className="w-full flex items-center justify-between text-sm font-body font-medium text-muted-foreground hover:text-foreground mt-2" onClick={() => { setOpen(false); setIsNotificationOpen(true); }}>
                <span className="flex items-center gap-2"><Bell className="w-4 h-4" /> {lang === 'zgh' ? "ⵉⵏⵖⵎⵉⵙⵏ ⵉⵏⵓ" : isRtl ? "إشعاراتي" : "Mes Alertes"}</span>
                {unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </button>
            )}
            {isAuthenticated ? (
              user?.role === "CITIZEN" ? (
                <Button size="sm" variant="outline" className="w-full font-body font-semibold mt-2 gap-2 text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => { logout(); setOpen(false); }}>
                  <User className="w-4 h-4" /> Déconnexion ({lang === 'zgh' ? "ⵉⵎⵎⴰⵏ" : isRtl ? "متصل" : "Connecté"})
                </Button>
              ) : (
                <div className="flex gap-2 w-full mt-2">
                  <Button size="sm" variant="secondary" className="flex-1 font-body font-semibold gap-1.5 px-2" asChild>
                    {user?.role === 'ADMIN' ? (
                      <Link to="/in" onClick={() => setOpen(false)}>
                        <LayoutDashboard className="w-4 h-4" /> {lang === 'zgh' ? "ⴰⵎⵉⴹⴰⵏ ⵉⵏⵓ" : isRtl ? "حسابي" : "Mon espace"}
                      </Link>
                    ) : (
                      <Link to="/dashboard" onClick={() => setOpen(false)}>
                        <LayoutDashboard className="w-4 h-4" /> {lang === 'zgh' ? "ⴰⵎⵉⴹⴰⵏ ⵉⵏⵓ" : isRtl ? "حسابي" : "Mon espace"}
                      </Link>
                    )}
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1 font-body font-semibold gap-1.5 px-2 text-red-600 border-red-200 hover:bg-red-50" onClick={() => { logout(); setOpen(false); }}>
                    Déconnexion
                  </Button>
                </div>
              )
            ) : (
              <Button size="sm" variant="secondary" className="w-full font-body font-semibold mt-2 gap-2" onClick={() => { setOpen(false); setIsAuthModalOpen(true); }}>
                <LogIn className="w-4 h-4" /> {lang === 'zgh' ? "ⴽⵛⵎ" : isRtl ? "تسجيل الدخول" : "Se connecter"}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* ─── DESKTOP DRAWER for Hamburger Mode ─── */}
      {open && navStyle === 'hamburger' && (
        <div className="hidden md:block absolute top-[65px] right-4 w-80 bg-background/95 backdrop-blur-3xl border border-border rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-top-4 p-4 z-50">
          <div className="flex justify-between items-center mb-4 px-2">
            <h3 className="font-display font-bold text-lg">{lang === 'zgh' ? "ⵓⵎⵓⵖ" : isRtl ? "القائمة" : "Menu"}</h3>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-muted text-muted-foreground rounded-md"><X className="w-4 h-4"/></button>
          </div>
          <div className="space-y-1">
            {links.map((link, idx) => {
              const label = lang === 'zgh' && link.label_zgh ? link.label_zgh : isRtl ? link.label_ar : link.label_fr;
              const hoverText = lang === 'zgh' && link.hover_zgh ? link.hover_zgh : isRtl ? link.hover_ar : link.hover_fr;
              const langStyles = lang === 'ar' ? 'text-lg tracking-normal' : lang === 'zgh' ? 'text-sm tracking-wide uppercase' : 'text-xs tracking-widest uppercase';
              const itemClass = `group flex items-center font-bold px-4 py-2.5 rounded-xl hover:bg-primary/5 hover:text-primary transition-colors ${langStyles}`;
              return link.href.startsWith("#") ? (
                <a key={idx} href={link.href} className={itemClass} onClick={() => setOpen(false)}>
                  <span>{label}</span>
                  {hoverText && (
                    <span className="ms-1 max-w-0 overflow-hidden group-hover:max-w-[200px] transition-all duration-300 opacity-0 group-hover:opacity-100 font-normal text-muted-foreground group-hover:text-primary whitespace-nowrap">
                      ({hoverText})
                    </span>
                  )}
                </a>
              ) : (
                <Link key={idx} to={link.href} className={itemClass} onClick={() => setOpen(false)}>
                  <span>{label}</span>
                  {hoverText && (
                    <span className="ms-1 max-w-0 overflow-hidden group-hover:max-w-[200px] transition-all duration-300 opacity-0 group-hover:opacity-100 font-normal text-muted-foreground group-hover:text-primary whitespace-nowrap">
                      ({hoverText})
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
          <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 gap-2">
            <Button size="sm" variant="default" className="w-full rounded-xl font-bold bg-red-600 hover:bg-red-700 text-white animate-pulse shadow-lg shadow-red-500/30 flex items-center justify-center gap-2" asChild>
               <Link to="/ring" onClick={() => setOpen(false)}>
                 <span className="w-2 h-2 rounded-full bg-white"></span>
                 {lang === 'zgh' ? "ⴰⴱⴰⵔⴰⵣ" : isRtl ? "الحلبة" : "Le Ring"}
               </Link>
            </Button>
            <Button size="sm" className="w-full rounded-xl font-bold shadow-sm bg-gradient-to-r from-rose-500 to-pink-600 text-white hover:from-rose-600 hover:to-pink-700" asChild>
               <Link to="/match" onClick={() => setOpen(false)} className="flex items-center justify-center gap-2">
                 <Flame className="w-4 h-4 text-yellow-300 animate-pulse" />
                 {lang === 'zgh' ? "ⵙⵡⴰⵢⴱ" : isRtl ? "سوايب" : "Swipe"}
               </Link>
            </Button>
          </div>
        </div>
      )}

      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
      
      <NotificationOverlay isOpen={isNotificationOpen} onClose={() => setIsNotificationOpen(false)} />
    </nav>
  );
};

export default Navbar;
